import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useApi } from "../../containers/ApiContainer";
import { useIdentity } from "../../containers/IdentityContainer";
import { useSnackbar } from "../../containers/SnackbarContainer";
import { useTokenMaker } from "../../hooks";
import { fetchAircraft } from "../../hooks/api";
import { useFAATailNumbers } from "../../hooks/useFAATailNumbers";
import { Tenant } from "../../types";
import {
  tailNumbersMatch,
  tailNumberStartsWith,
  tailNumberIncludes,
  stripHyphens,
} from "../../utils/tailNumberUtils";

const levenshteinDistance = (s: string, t: string) => {
  if (!s.length) return t.length;
  if (!t.length) return s.length;
  const arr = [];
  for (let i = 0; i <= t.length; i++) {
    arr[i] = [i];
    for (let j = 1; j <= s.length; j++) {
      arr[i][j] =
        i === 0
          ? j
          : Math.min(
              arr[i - 1][j] + 1,
              arr[i][j - 1] + 1,
              arr[i - 1][j - 1] + (s[j - 1] === t[i - 1] ? 0 : 1)
            );
    }
  }
  return arr[t.length][s.length];
};

type Props = {
  disableAutocomplete: boolean;
  defaultValue?: string;
  warning?: string;
  tenants?: Array<Tenant>;
  onInputChange?: (tailNumber: string) => void;
  onChange?: (tenant: Tenant) => void;
  autoFocus?: boolean;
};

const RGX_VALID_REGISTRATIONS = /[A-Z]/;

export const SelectTailNumber: React.FC<Props> = ({
  disableAutocomplete,
  defaultValue = "",
  warning,
  tenants = [],
  onChange = () => {},
  onInputChange = () => {},
  autoFocus = false,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const { postgrest } = useApi();
  const { airplxIdentity } = useIdentity();
  const { url: tokenMakerURL } = useTokenMaker();
  const snackbar = useSnackbar();
  // Initialize with defaultValue to ensure filtering works on first render
  const [value, setValue] = React.useState<string>(defaultValue);
  const [debouncedValue, setDebouncedValue] = React.useState<string>(defaultValue);
  const [requestDialogOpen, setRequestDialogOpen] =
    React.useState<boolean>(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    React.useState<boolean>(false);
  const [tailNumberToRequest, setTailNumberToRequest] =
    React.useState<string>("");
  const [isFocused, setIsFocused] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  // Debounce the value to synchronize filtering with FAA API data fetch (300ms)
  // This prevents the dropdown from "bouncing" when FAA data arrives
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, 300);

    return () => clearTimeout(timer);
  }, [value]);

  // Memoize the exclude list to prevent unnecessary re-renders of the hook
  const excludeTailNumbers = React.useMemo(
    () => tenants.map((t) => t.tail_number),
    [tenants]
  );

  // useFAATailNumbers is used to fetch FAA tail numbers. exclude the ones that are already in the tenants list
  // to avoid duplicates in the autocomplete list
  const { loading, tailNumbers } = useFAATailNumbers(value, excludeTailNumbers);

  const tenantsForAutocomplete = React.useMemo(() => {
    return [...tenants, ...tailNumbers].filter(
      (t) => t.tail_number?.length > 2
    );
  }, [tenants, tailNumbers]);

  // Memoize sorted options to prevent recalculation on every render
  // Uses debouncedValue to sync with FAA API data arrival
  const sortedOptions = React.useMemo(() => {
    // If no input, return alphabetically sorted list
    if (!debouncedValue || debouncedValue.length < 1) {
      return [...tenantsForAutocomplete].sort((a, b) =>
        a.tail_number.localeCompare(b.tail_number)
      );
    }

    const valueUpper = debouncedValue.toUpperCase();

    // Sort with prefix-aware scoring (hyphen-flexible)
    return [...tenantsForAutocomplete].sort((a, b) => {
      const aNum = a.tail_number.toUpperCase();
      const bNum = b.tail_number.toUpperCase();

      // Priority 1: Exact match (ignoring hyphens)
      const aExact = tailNumbersMatch(aNum, valueUpper) ? 0 : 1;
      const bExact = tailNumbersMatch(bNum, valueUpper) ? 0 : 1;
      if (aExact !== bExact) return aExact - bExact;

      // Priority 2: Starts with (prefix match, ignoring hyphens) - prioritizes C-AAC for "C" or "CAAC"
      const aStarts = tailNumberStartsWith(aNum, valueUpper) ? 0 : 1;
      const bStarts = tailNumberStartsWith(bNum, valueUpper) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;

      // Priority 3: Levenshtein distance for fuzzy matching (using normalized tail numbers)
      const aDist = levenshteinDistance(stripHyphens(valueUpper), stripHyphens(aNum));
      const bDist = levenshteinDistance(stripHyphens(valueUpper), stripHyphens(bNum));
      if (aDist !== bDist) return aDist - bDist;

      // Priority 4: Alphabetical
      return aNum.localeCompare(bNum);
    });
  }, [tenantsForAutocomplete, debouncedValue]);

  // Compute filtered options based on current value (not debounced)
  // This ensures the banner check matches what the Autocomplete dropdown shows
  const filteredOptions = React.useMemo(() => {
    if (!value || value.length < 1) return sortedOptions;
    return sortedOptions.filter((o) => tailNumberIncludes(o.tail_number, value));
  }, [sortedOptions, value]);

  // Check if the current value is not found in registry
  // Uses current `value` (not debounced) to stay in sync with Autocomplete dropdown
  // Only shows banner when there are NO filtered results visible
  const isNotInRegistry = React.useMemo(() => {
    if (!value || value.length < 2) return false;
    if (loading) return false; // Don't show banner while still loading FAA data
    return filteredOptions.length === 0;
  }, [value, loading, filteredOptions]);

  // Handle aircraft request submission
  const handleAircraftRequest = async () => {
    try {
      // Guard against making requests before auth is ready
      if (!postgrest) {
        snackbar.notify({
          text: t('widgets.selectTailNumber.waitForCredentials'),
          severity: "warning",
        });
        return;
      }

      setIsSubmitting(true);

      // First, insert into database via PostgREST
      const { error: dbError } = await postgrest
        .from("tail_number_requests")
        .insert({
          tail_number: tailNumberToRequest,
          fbo_id: activeFBO?.id,
          requested_by: airplxIdentity.userid,
        });

      if (dbError) {
        console.error("Failed to save request to database:", dbError);
        throw new Error("Database insert failed");
      }

      // Then, send email notification (optional - don't fail if email service is down)
      try {
        const params = new URLSearchParams({
          fbo_name: activeFBO?.name || "Unknown FBO",
          user_email: airplxIdentity.email,
          make: "Unknown",
          model: "Unknown",
          tail_number: tailNumberToRequest,
          uniqueMods: "Tail number not in registry",
          timeframe: "ASAP",
        });

        await fetch(`${tokenMakerURL}/request_aircraft?${params.toString()}`);
      } catch (emailError) {
        // Email failed but request was saved to database - that's fine
        console.warn("Email notification failed, but request was saved:", emailError);
      }

      setRequestDialogOpen(false);
      setConfirmationDialogOpen(true);
    } catch (error) {
      console.error("Failed to submit aircraft request:", error);
      setRequestDialogOpen(false);
      snackbar.notify({
        text: t('widgets.selectTailNumber.requestErrorMessage'),
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  React.useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  if (disableAutocomplete) {
    return (
      <>
        <Stack direction="column" sx={{ width: "100%" }}>
          <TextField
            inputProps={{ testid: "tail_number" }}
            fullWidth
            size="small"
            autoFocus={autoFocus}
            label={t('widgets.selectTailNumber.tailNumberLabel')}
            onChange={(evt) => {
              let newInputValue = evt.target.value.toLocaleUpperCase();
              // Only auto-prepend "N" if user is typing numbers and there's actual content
              if (
                newInputValue &&
                !RGX_VALID_REGISTRATIONS.test(newInputValue)
              ) {
                // default to N since it is the most common
                newInputValue = "N" + newInputValue;
              }
              onInputChange(newInputValue);
              setValue(newInputValue);
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            value={value}
          />{" "}
          {isNotInRegistry && value.length >= 2 && isFocused && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                mt: 1,
                p: 1.5,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                minHeight: 48,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  flex: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t('widgets.selectTailNumber.notInRegistryShort')}
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                sx={{ flexShrink: 0 }}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur from firing
                  setTailNumberToRequest(value);
                  setRequestDialogOpen(true);
                }}
              >
                {t('widgets.selectTailNumber.requestButton')}
              </Button>
            </Stack>
          )}
          <Typography variant="caption" color="error">
            {warning}
          </Typography>
        </Stack>

        {/* Aircraft Request Dialog */}
        <Dialog
          open={requestDialogOpen}
          onClose={() => !isSubmitting && setRequestDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('widgets.selectTailNumber.requestDialogTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('widgets.selectTailNumber.requestDialogDescription')}
            </DialogContentText>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                disabled
                label={t('widgets.selectTailNumber.tailNumberFieldLabel')}
                value={tailNumberToRequest}
                fullWidth
              />
              <TextField
                disabled
                label={t('widgets.selectTailNumber.fboLabel')}
                value={activeFBO.name}
                fullWidth
              />
              <TextField
                disabled
                label={t('widgets.selectTailNumber.requestedByLabel')}
                value={airplxIdentity.email}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setRequestDialogOpen(false)}
              disabled={isSubmitting}
            >
              {t('widgets.selectTailNumber.cancelButton')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAircraftRequest}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t('widgets.selectTailNumber.submitting')
                : t('widgets.selectTailNumber.submitRequestButton')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmationDialogOpen}
          onClose={() => setConfirmationDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('widgets.selectTailNumber.confirmationTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText
              dangerouslySetInnerHTML={{
                __html: t('widgets.selectTailNumber.confirmationMessage', { tailNumber: tailNumberToRequest })
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => setConfirmationDialogOpen(false)}
            >
              {t('widgets.selectTailNumber.gotItButton')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Stack direction="column" sx={{ width: "100%" }}>
        <Autocomplete
          size="small"
          fullWidth
          freeSolo
          openOnFocus
          inputValue={value}
          options={filteredOptions}
          getOptionLabel={(option: Tenant | string) => {
            if (typeof option === "string") {
              return option;
            }
            return option.tail_number;
          }}
          filterOptions={(options) => options}
          ListboxProps={{
            style: {
              maxHeight: '40vh',
              overflow: 'auto',
            },
          }}
          slotProps={{
            popper: {
              sx: {
                zIndex: 10000, // Higher than dialog (9600)
              },
            },
          }}
          onInputChange={(event: any, newInputValue: string) => {
            newInputValue = newInputValue.toLocaleUpperCase();
            // Only auto-prepend "N" if user is typing numbers and there's actual content
            if (newInputValue && !RGX_VALID_REGISTRATIONS.test(newInputValue)) {
              // default to N since it is the most common
              newInputValue = "N" + newInputValue;
            }
            onInputChange(newInputValue);
            setValue(newInputValue);
          }}
          onChange={async (event: any, tenant: Tenant | string | null) => {
            // With freeSolo mode, tenant can be a string (when typing/pasting) or null (when clearing)
            // Only call onChange when we have a valid Tenant object
            if (!tenant || typeof tenant === "string") {
              return;
            }
            if (!tenant?.aircraft && tenant?.aircraft_id) {
              // if the tenant has an aircraft_id but no aircraft, we need to fetch it
              const aircraft = await fetchAircraft(
                postgrest,
                tenant.aircraft_id,
                activeFBO
              );
              tenant.aircraft = aircraft;
            }
            if (tenant && !tenant?.type) {
              tenant.type = "transient"; // default type if not set
            }
            onChange(tenant);
          }}
          value={value}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              autoFocus={autoFocus}
              label={t('widgets.selectTailNumber.tailNumberLabel')}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              inputProps={{
                testid: "tail_number",
                ...params.inputProps,
                onKeyDown: (e) => {
                  // i don't want user to ever hit enter in this form
                  if (e.key === "Enter") {
                    e.stopPropagation();
                  }
                },
              }}
            />
          )}
        />
        {isNotInRegistry && value.length >= 2 && isFocused && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              mt: 1,
              p: 1,
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              minHeight: 40,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
                mr: 1,
              }}
            >
              {t('widgets.selectTailNumber.notInRegistryLong')}
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="primary"
              sx={{ flexShrink: 0 }}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur from firing
                setTailNumberToRequest(value);
                setRequestDialogOpen(true);
              }}
            >
              {t('widgets.selectTailNumber.requestButton')}
            </Button>
          </Stack>
        )}
      </Stack>
      {/* Aircraft Request Dialog */}
      <Dialog
        open={requestDialogOpen}
        onClose={() => !isSubmitting && setRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('widgets.selectTailNumber.requestDialogTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('widgets.selectTailNumber.requestDialogDescription')}
          </DialogContentText>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              disabled
              label={t('widgets.selectTailNumber.tailNumberFieldLabel')}
              value={tailNumberToRequest}
              fullWidth
            />
            <TextField disabled label={t('widgets.selectTailNumber.fboLabel')} value={activeFBO.name} fullWidth />
            <TextField
              disabled
              label={t('widgets.selectTailNumber.requestedByLabel')}
              value={airplxIdentity.email}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRequestDialogOpen(false)}
            disabled={isSubmitting}
          >
            {t('widgets.selectTailNumber.cancelButton')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAircraftRequest}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t('widgets.selectTailNumber.submitting')
              : t('widgets.selectTailNumber.submitRequestButton')}
          </Button>
        </DialogActions>
      </Dialog>{" "}
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('widgets.selectTailNumber.confirmationTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText
            dangerouslySetInnerHTML={{
              __html: t('widgets.selectTailNumber.confirmationMessage', { tailNumber: tailNumberToRequest })
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setConfirmationDialogOpen(false)}
          >
            {t('widgets.selectTailNumber.gotItButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
